'use strict';
/* ==========================================================================
   levels/ballad.js — step "ballad" (any): THE BALLAD, "The Schoolhouse Gate" (DESIGN.md §5.4), a 1960s folk-protest
   ballad as a paper-collage story with karaoke. Built from lessons/branches/levels/march.js (same cast, same controls).

   ► The song: assets/music/ballad.m4a = ACE-Step take folk_7 (rev-2 lyrics, 96 s; assets/ASSETS.md, CHOICE.md).
     Timings: assets/music/ballad_words.json (tools/song_timing.py): the TRUE lyric lines of content/song.json with
     word times from the take's Whisper passes, and the beat grid {bpm 95, offset = first downbeat ≈ 1.38 s}.
     The scene is keyed ONLY to that file: every picture beat is a sung word (S.W(line, word)).

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing), so seeking, pausing and the QA screenshots are exact. Every case year and vote
   comes from core-local/rights.js (Rights.CASES); the five freedoms from core-local/civic.js (Civic.freedom).

   The stage (always on): parchment, bunting that sways on the beat, a kraft road, and the SCHOOLHOUSE GATE (two brick
   pillars, an iron double gate, a "PUBLIC SCHOOL" arch sign: a public school is government). The gate stands in
   verse 1, steps back while the chorus / verse 2 / bridge fill the stage, and swings OPEN in the outro.
   Acts, keyed to lyric-line indices (content/song.json order):
     verse1   0–3    the title card; an Iowa winter (paper snow, snow on the road); 1965 + "Des Moines, Iowa"; the black
                     armband (armband art) + a "13" tag on "thirteen"; a megaphone crossed out ("didn't shout") and
                     "a silent protest"; the Tinker docket slides in on "Court", its seal (CASES year / vote) on "side"
     chorus   4–7, 14–17   the five freedom badges: religion on "rights", then speech / press / assembly / petition on
                     "Speak it, print it, gather, petition"; "The First Amendment binds" + a GOVERNMENT stamp on "binds";
                     grey PRIVATE badges (store, parents, app) pop on their words and are crossed out
     verse2   8–11   the school paper (newspaper art) with a dashed CUT line (Hazelwood, from CASES); a TRUE THREAT card
                     stamped NOT PROTECTED; an "unpopular opinion" placard (protest_sign art) stamped PROTECTED; the
                     speech badge on "speech"
     bridge   12–13  the rights scroll (scroll_rights art) on "Bill"; three docket slips: Miranda "stay silent",
                     Gideon "a lawyer", Gault "a fair hearing" (each on its word; CASES year / vote on the seals)
     outro    18–19  the gate is back; the five badges line up on "rights" and go through as the gate swings open on
                     "Through … gate"; RATIFIED + a wax seal
   Karaoke (bottom band, torn cream paper): the current line in woodtype, the sung word on a mustard scrap, freedom
   words in their freedom colour, "schoolhouse gate" in ransom letters, the next line faint below.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom;
   "Finish" once ≥ 90 % has been heard. The song's end (≥ 90 % heard) → Game.complete('ballad').
   Portrait phone: the stage on top, Dock.karaoke + ↺ ▶ ⌂ + the strip below.
   Long music (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared
   <audio> element (Audio.media), never decoded into memory.

   QA hooks: sc.song (S(i), E(i), W(i, word), beats), sc.plan.acts, sc.debugAt(t) (freeze at song time t), sc.now(),
   sc.play(t), sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done() / sc.finishStub() (stamp now),
   sc.lyricAt(t), sc.activeWord(t), sc.actAt(t), sc.gateAt(t) ({alpha, open}), sc.badgesAt(t) (freedom ids showing),
   sc.heard, sc.ended, sc.audioReady.
   ========================================================================== */
(function () {
  const STEP = 'ballad';
  const TAKE_NAME = 'folk_7';
  const AUDIO = 'music/ballad.m4a', WORDS = 'music/ballad_words.json';
  const KEY = 'song:ballad';
  const WOOD = "'Alfa Slab One'", TYPE = "'Special Elite'";
  const CC = () => (window.Civic ? Civic.C : { navy: '#1f2f55', brick: '#9e2b25', cream: '#f3ead7', gold: '#b08a2e', parchment: '#efe2c2', wax: '#8e1f1b', ink: '#2b2420', kraft: '#c99a67', plum: '#6b3a6b', green: '#2e5a4f' });
  const GOLD_TEXT = '#86651a';                       // old gold, dark enough to read as text on cream
  const GREY = '#8d8781';                            // the PRIVATE badges
  const BAND_Y = 832, CUR_Y = 912, NEXT_Y = 1012, CUR_SIZE = 56, NEXT_SIZE = 36;
  const ROAD_Y = 770;                                 // the gate's base line
  const FADE = 0.35;                                  // act cross-fade (s)
  const THRESH = 0.9;                                 // share of the song heard for the stamp / "Finish"
  const CREDIT = 'AI-generated song · the civics is real';
  const END_NOTE = 'Your rights come with you.';

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines; this.dur = d.duration || 96;
      const b = d.beats || {};
      this.bpm = b.bpm || 95; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      this.items = d.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j }))).sort((a, c) => a.s - c.s);
    }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) return 1e6;
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    pulse(t, k = 9) { return t < this.off - 0.05 ? 0 : Math.exp(-this.sinceBeat(t) * k); }
    singing(t) { return this.lines.some(l => t >= l.s - 0.05 && t <= l.e + 0.1); }
  }

  /* ---------------- the cases (the model only) ---------------- */
  const CASE = id => { const R = window.Rights; const K = R && R.CASES && R.CASES[id]; if (!K) throw new Error('no case ' + id); return K; };
  const VOTE = v => String(v).replace('-', '–');

  /* ---------------- the plan: acts and cue times ---------------- */
  function plan(S) {
    const b = i => S.S(i) - 0.3;
    const acts = [
      { id: 'verse1', at: 0, draw: actVerse1 },
      { id: 'chorus1', at: b(4), draw: actChorus, c0: 4 },
      { id: 'verse2', at: b(8), draw: actVerse2 },
      { id: 'bridge', at: b(12), draw: actBridge },
      { id: 'chorus2', at: b(14), draw: actChorus, c0: 14 },
      { id: 'outro', at: b(18), draw: actOutro },
    ];
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur + 1; });
    // the gate: up in verse 1 and through each chorus's first line, back in the outro; opens on "Through … gate"
    const gate = [
      [0, b(0), S.S(5) - 0.2, S.S(5) + 0.3],
      [b(14), b(14) + 0.4, S.S(15) - 0.2, S.S(15) + 0.3],
      [b(18), b(18) + 0.5, S.dur + 5, S.dur + 6],
    ];
    const tOpen0 = S.W(19, 'Through'), tOpen1 = S.W(19, 'gate') + 0.35;
    return { acts, ticks: acts.slice(1).map(a => a.at), gate, tOpen0, tOpen1, tStamp: S.E(19) + 0.9 };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }
  function gateAt(S, p, t) {
    let a = 0;
    for (const [a0, a1, b0, b1] of p.gate) if (t >= a0 && t < b1) a = Math.max(a, (a0 <= 0 ? 1 : P(t, a0, a1)) * (1 - P(t, b0, b1)));
    return { alpha: a, open: E.inOutCubic(P(t, p.tOpen0, p.tOpen1)) };
  }

  /* ---------------- drawing helpers ---------------- */
  const layoutCache = new Map();
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  function measure(str, font) {
    const k = font + '|' + str;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  function ransomW(str, size, seed) {
    const k = 'R|' + str + '|' + size + '|' + seed;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); w = ransom(str, -9999, -9999, size, 1e9, { seed }); g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  /* the engine's ink() sets globalAlpha itself: pass the current (faded) alpha through */
  const inkA = (pts, o = {}) => ink(pts, { ...o, alpha: (o.alpha ?? 1) * g.globalAlpha });
  function withAlpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  function wood(str, x, y, size, t0, o = {}) {
    if (!window.Civic) { ransom(str, x, y, size, t0, { seed: o.seed || 1 }); return; }
    Civic.woodtype(str, x, y, size, { t0, ...o });
  }
  /* an ink X over (x, y), drawn on from t0 */
  function crossOut(x, y, r, t, t0, seed) {
    if (t < t0) return;
    const C = CC();
    inkA([[x - r, y - r], [x + r, y + r]], { w: 10, color: C.brick, seed, prog: E.outCubic(P(t, t0, t0 + 0.18)), passes: 2 });
    inkA([[x + r, y - r], [x - r, y + r]], { w: 10, color: C.brick, seed: seed + 1, prog: E.outCubic(P(t, t0 + 0.12, t0 + 0.3)), passes: 2 });
  }
  function stampAt(text, x, y, t, t0, { s = 1, rot = -0.1, color } = {}) {
    if (t < t0) return;
    UI.stamp(text, x, y, { rot, s: pop(T, t0, 0.3, 2.2) * s, color: color || CC().brick });
  }
  function freedomBadge(id, x, y, size, t, t0, seed, o = {}) {
    if (t < t0 || !window.Civic) return;
    const beat = o.pulse ? 1 + 0.05 * o.pulse : 1;
    Civic.freedom(id, x, y, size, { s: pop(T, t0, 0.35, 2.4) * beat, seed, label: o.label !== false, t0: t0 + 0.15, rot: o.rot || 0 });
  }

  /* ---------------- the stage: background, bunting, road, snow, the gate ---------------- */
  const SNOW = (() => { const r = rng(9021), out = []; for (let i = 0; i < 70; i++) out.push({ x: r() * 1960 - 20, v: 40 + r() * 50, ph: r() * TAU, sw: 12 + r() * 26, r: 4 + Math.round(r() * 5), d: r() * 20 }); return out; })();
  function snowA(S, t) { return 1 - P(t, S.S(4) - 0.6, S.S(4) + 0.4); }
  function backdrop(S, p, t, docked) {
    const C = CC();
    g.fillStyle = paperTile(C.parchment, 6); g.fillRect(0, 0, 1920, 1080);
    const pu = S.pulse(t, 6), x1 = docked ? 1890 : Math.min(1580, UI.hudLeft(3) - 30);
    if (window.Civic) {
      g.save(); g.translate(0, 5 * pu);
      Civic.bunting(docked ? 30 : 470, x1, 4, { sag: docked ? 12 : 20, n: docked ? 17 : 11, seed: 5, sway: 1 + pu });
      g.restore();
    }
    // the road, and snow on it in verse 1
    paperRect(960, ROAD_Y + 30, 2080, 120, C.kraft, { seed: 21, torn: true, depth: 1.1, boil: false, tex: 'tex_kraft' });
    const sa = snowA(S, t);
    withAlpha(sa, () => paperRect(960, ROAD_Y - 2, 2080, 34, PAL.white, { seed: 24, torn: true, depth: 0.5, boil: false }));
    if (docked) paperRect(960, 1010, 2080, 150, C.cream, { seed: 22, torn: true, depth: 0.8, boil: false });
  }
  function snow(S, t) {
    const a = snowA(S, t); if (a <= 0.001) return;
    g.save(); g.globalAlpha *= 0.9 * a; g.fillStyle = '#ffffff'; g.strokeStyle = 'rgba(43,36,32,0.35)'; g.lineWidth = 1.5;
    for (const f of SNOW) {
      const y = ((f.d + t) * f.v) % 820 - 20, x = f.x + Math.sin(t * 0.9 + f.ph) * f.sw;
      g.beginPath(); g.arc(x, y, f.r, 0, TAU); g.fill(); g.stroke();
    }
    g.restore();
  }
  /* the schoolhouse gate: pillars at ±GX, iron leaves hinged on the pillars; open ∈ [0, 1] swings them in */
  const GX = 250, GH = 330, PW = 76;
  function gate(S, t, G) {
    if (G.alpha <= 0.001) return;
    const C = CC(), pu = S.pulse(t, 7);
    withAlpha(G.alpha, () => {
      // the iron leaves (behind the pillars): each leaf is drawn in its hinge's frame, x-scaled by cos(angle)
      const sx = Math.max(0.05, Math.cos(G.open * 1.35));
      for (const side of [-1, 1]) {
        g.save(); g.translate(960 + side * (GX - PW / 2), ROAD_Y); g.scale(-side * sx, 1);
        const L = GX - PW / 2, top = -GH + 70;
        const bars = [];
        for (let k = 0; k <= 7; k++) bars.push(k * L / 7);
        bars.forEach((bx, k) => inkA([[bx, -6], [bx, top - (k % 2 ? 10 : 26)]], { w: 7, color: '#2a2622', seed: 1200 + k + (side > 0 ? 20 : 0), passes: 1, step: 40, amp: 0.4 }));
        [-18, -GH * 0.45, top + 18].forEach((yy, k) => inkA([[0, yy], [L, yy]], { w: 8, color: '#2a2622', seed: 1240 + k + (side > 0 ? 20 : 0), passes: 1, step: 50, amp: 0.4 }));
        // finials (arrow-heads on alternate bars)
        bars.forEach((bx, k) => { if (k % 2 === 0) inkA([[bx - 9, top - 14], [bx, top - 34], [bx + 9, top - 14]], { w: 5, color: '#2a2622', seed: 1260 + k, passes: 1, step: 10, amp: 0.3 }); });
        g.restore();
      }
      // the pillars + caps
      for (const side of [-1, 1]) {
        const x = 960 + side * GX;
        paperRect(x, ROAD_Y - GH / 2, PW, GH, C.brick, { seed: 1280 + (side > 0 ? 1 : 0), depth: 1.1, boil: false });
        for (let k = 1; k < 7; k++) inkA([[x - PW / 2 + 6, ROAD_Y - k * GH / 7], [x + PW / 2 - 6, ROAD_Y - k * GH / 7]], { w: 2, color: 'rgba(40,14,10,0.45)', seed: 1290 + k + (side > 0 ? 10 : 0), passes: 1, step: 40, amp: 0.3 });
        paperRect(x, ROAD_Y - GH - 10, PW + 26, 26, C.cream, { seed: 1300 + (side > 0 ? 1 : 0), depth: 0.7, boil: false });
        paperCircle(x, ROAD_Y - GH - 36, 20, C.cream, { seed: 1302 + (side > 0 ? 1 : 0), depth: 0.6 });
      }
      // the arch sign, bobbing a little on the beat
      g.save(); g.translate(960, ROAD_Y - GH - 38 - 3 * pu);
      inkA([[-GX, 40], [-GX * 0.55, -8], [0, -22], [GX * 0.55, -8], [GX, 40]], { w: 6, color: '#2a2622', seed: 1310, passes: 1, step: 30, amp: 0.4 });
      wood('PUBLIC SCHOOL', 0, 2, 34, -1e6, { color: C.cream, ink: C.navy, seed: 1311, stagger: 0 });
      g.restore();
    });
  }

  /* ======================================================================
     the acts (overlays on the stage)
     ====================================================================== */
  /* verse 1: the Iowa cold, the armband, 13, silent, the Tinker docket */
  function actVerse1(S, t) {
    const C = CC();
    const tSix = S.W(0, 'sixty'), tIo = S.W(0, 'Iowa'), tMa = S.W(1, 'Mary'), tArm = S.W(1, 'armband'), tTh = S.W(1, 'thirteen');
    const tSh = S.W(2, 'shout'), tDi = S.W(2, 'didnt'), tSat = S.W(2, 'sat'), tCo = S.W(3, 'Court'), tSide = S.W(3, 'side');
    // the title card until the year comes in
    withAlpha(1 - P(t, tSix - 0.4, tSix), () => {
      ransom('THE SCHOOLHOUSE GATE', 960, 215, 72, 0.1, { seed: 902 });
      hand('a folk ballad', 960, 300, 46, 0.5, { color: C.navy, seed: 903, dur: 0.4 });
    });
    // 1965, Des Moines, Iowa
    if (t >= tSix) wood('1965', 330, 250, 84, tSix, { color: C.cream, ink: C.navy, seed: 910, stagger: 0.05 });
    if (t >= tIo) hand('Des Moines, Iowa', 330, 350, 50, tIo, { color: C.ink, seed: 911, dur: 0.5 });
    // Mary Beth Tinker + the armband + "13"
    if (t >= tMa) hand('Mary Beth Tinker', 1540, 560, 62, tMa, { color: C.navy, seed: 912, dur: 0.5 });
    if (t >= tArm && window.Civic) Civic.prop('armband', 960, 628, 150, { s: pop(T, tArm, 0.35, 2.2), rot: -0.06, seed: 913 });
    if (t >= tTh) {
      const s = pop(T, tTh, 0.35, 2.4);
      inkA([[1030, 650], [1060, 672]], { w: 3, color: C.ink, seed: 914, prog: E.outCubic(P(t, tTh, tTh + 0.25)) });
      g.save(); g.translate(1095, 700); g.rotate(0.12); g.scale(s, s);
      paperRect(0, 0, 118, 82, C.kraft, { seed: 915, depth: 0.8, boil: false });
      paperCircle(-40, -24, 7, C.parchment, { seed: 916, depth: 0 });
      g.font = `56px ${WOOD}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = C.ink; g.fillText('13', 8, 4);
      g.restore();
    }
    // "didn't shout": a megaphone, crossed out; then "a silent protest"
    if (t >= Math.min(tSh, tDi + 0.3) && window.Civic) {
      const t0 = Math.min(tSh, tDi + 0.3);
      Civic.prop('megaphone', 400, 560, 170, { s: pop(T, t0, 0.3, 2.2), rot: 0.1, seed: 917 });
      crossOut(400, 560, 80, t, t0 + 0.25, 918);
    }
    if (t >= tSat) hand('a silent protest', 400, 690, 52, tSat, { color: C.brick, seed: 919, dur: 0.5 });
    // the Supreme Court took her side: the Tinker docket (CASES year / vote on the seal)
    if (t >= tCo - 0.2 && window.Civic) {
      const K = CASE('tinker'), u = E.outCubic(P(t, tCo - 0.2, tCo + 0.35));
      Civic.docket(lerp(2300, 1530, u), 290, 600, 230, { name: K.name, year: K.year, vote: VOTE(K.vote), eoc: K.eoc,
        lines: ['The students won.', 'A silent protest counts.'], rot: 0.02, seed: 920, t0: tSide });
    }
  }

  /* the chorus: the five freedoms; the First Amendment binds GOVERNMENT; not the store, parents, the app */
  const BADGE_Y = 240, BADGE_SIZE = 150, BADGE_X = id => 560 + Civic.FREEDOMS.indexOf(id) * 200;
  const PRIVATE = [['store', 'STORE'], ['parents', 'PARENTS'], ['app', 'APP']];
  function privateBadge(kind, label, x, y, size, s, seed) {
    if (s <= 0.001) return;
    const C = CC();
    g.save(); g.translate(x, y); g.scale(s, s);
    paperCircle(0, 0, Math.round(size / 2), GREY, { seed, depth: 1 });
    inkCircle(0, 0, size * 0.43, { w: 2, color: 'rgba(243,234,215,0.5)', seed: seed + 1, passes: 1 });
    const u = size * 0.5;
    if (kind === 'store') {           // an awning over a shop window
      paperRect(0, u * 0.18, Math.round(u * 0.9), Math.round(u * 0.6), C.cream, { seed: seed + 2, depth: 0.3, boil: false });
      for (let k = 0; k < 4; k++) paperRect(-u * 0.39 + k * u * 0.26, -u * 0.26, Math.round(u * 0.26), Math.round(u * 0.26), k % 2 ? C.cream : '#5d5853', { seed: seed + 3 + k, depth: 0.2, boil: false });
    } else if (kind === 'parents') {
      Civic.icon('people', 0, 0, u * 1.25, { seed: seed + 2, color: C.cream });
    } else {                          // a phone
      paperRect(0, 0, Math.round(u * 0.62), Math.round(u * 1.1), C.cream, { seed: seed + 2, depth: 0.3, boil: false });
      paperRect(0, -u * 0.04, Math.round(u * 0.48), Math.round(u * 0.78), '#5d5853', { seed: seed + 3, depth: 0, boil: false });
      paperCircle(0, u * 0.44, 4, '#5d5853', { seed: seed + 4, depth: 0 });
    }
    g.restore();
    // the tag under it
    g.save(); g.translate(x, y + size * 0.5 * s + 26); g.scale(s, s);
    g.font = `24px ${WOOD}`; const w = Math.round(measure(label, g.font) + 24);
    paperRect(0, 0, w, 38, GREY, { seed: seed + 9, depth: 0.5, boil: false });
    g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = C.cream; g.fillText(label, 0, 2);
    g.restore();
  }
  function badgeTimes(S, c0) {
    return { religion: S.W(c0, 'rights'), speech: S.W(c0 + 1, 'Speak'), press: S.W(c0 + 1, 'print'), assembly: S.W(c0 + 1, 'gather'), petition: S.W(c0 + 1, 'petition') };
  }
  function actChorus(S, t, A) {
    const C = CC(), c0 = A.c0, bt = badgeTimes(S, c0), pu = S.pulse(t, 7);
    if (!window.Civic) return;
    Civic.FREEDOMS.forEach((id, i) => freedomBadge(id, BADGE_X(id), BADGE_Y, BADGE_SIZE, t, bt[id], 950 + i * 9, { pulse: pu, rot: [-0.05, 0.03, -0.02, 0.04, -0.03][i] }));
    const tFi = S.W(c0 + 2, 'First'), tGov = S.W(c0 + 2, 'government'), tBi = S.W(c0 + 2, 'binds');
    if (t >= tGov - 0.1) hand('The First Amendment binds', 620, 470, 58, Math.min(tGov - 0.1, tFi), { color: C.ink, seed: 960, dur: 0.6 });
    if (t >= tBi - 0.05) {
      inkA([[930, 470], [1010, 470]], { w: 6, color: C.navy, seed: 961, prog: E.outCubic(P(t, tBi - 0.05, tBi + 0.2)) });
      stampAt('GOVERNMENT', 1270, 470, t, tBi, { s: 0.72, rot: -0.06, color: C.navy });
    }
    // not the store, not your parents, not the app: grey PRIVATE badges, crossed out
    PRIVATE.forEach(([kind, label], i) => {
      const t0 = S.W(c0 + 3, kind === 'app' ? 'apps' : kind), x = 640 + i * 320, y = 628;
      if (t < t0) return;
      privateBadge(kind, label, x, y, 118, pop(T, t0, 0.3, 2.2), 970 + i * 11);
      crossOut(x, y, 58, t, t0 + 0.2, 990 + i * 3);
    });
    const tNot = S.W(c0 + 3, 'Not');
    if (t >= tNot) hand('private:', 380, 628, 50, tNot, { color: GREY, seed: 999, dur: 0.3 });
    const tLi = S.W(c0 + 3, 'lines');
    if (t >= tLi) hand('not bound by it', 1600, 628, 46, tLi, { color: C.brick, seed: 998, dur: 0.4 });
  }

  /* verse 2: the school paper (CUT), a true threat (NOT PROTECTED), an unpopular opinion (PROTECTED) */
  function actVerse2(S, t) {
    const C = CC();
    if (!window.Civic) return;
    const tPa = S.W(8, 'paper'), tCl = S.W(8, 'class'), tTr = S.W(8, 'trim');
    const tTh = S.W(9, 'threat'), tPr = S.W(9, 'protected'), tUn = S.W(10, 'unpopular'), tLet = S.W(10, 'let'), tSp = S.W(11, 'speech');
    // the school paper, with a dashed CUT line on "trim"
    if (t >= tPa) {
      Civic.prop('newspaper', 400, 400, 300, { s: pop(T, tPa, 0.35, 2.2), rot: -0.05, seed: 1010 });
      hand('the class paper', 400, 590, 48, tCl, { color: C.ink, seed: 1011, dur: 0.4 });
      const K = CASE('hazelwood');
      if (t >= tCl + 0.3) hand(`${K.name}, ${K.year}`, 400, 650, 34, tCl + 0.3, { color: C.navy, seed: 1012, dur: 0.5, font: TYPE });
    }
    if (t >= tTr) {
      const u = E.outCubic(P(t, tTr, tTr + 0.4));
      for (let k = 0; k < 9; k++) { const x0 = 220 + k * 42, a = clamp((u * 9) - k); if (a > 0) inkA([[x0, 450 + k * -4], [x0 + 24 * a, 450 + (k + a) * -4]], { w: 6, color: C.brick, seed: 1013 + k, passes: 1, step: 20 }); }
      stampAt('CUT', 470, 380, t, tTr + 0.15, { s: 0.6, rot: -0.14 });
    }
    // a true threat: NOT PROTECTED
    if (t >= tTh - 0.1) {
      const s = pop(T, tTh - 0.1, 0.35, 2.2);
      Civic.parchment(960, 330, 440, 170, { seed: 1030, rot: 0.02, s });
      if (s > 0.9) wood('TRUE THREAT', 960, 330, 48, tTh, { color: C.cream, ink: C.ink, seed: 1031, stagger: 0.02 });
    }
    stampAt('NOT PROTECTED', 960, 460, t, tPr, { s: 0.58, rot: -0.08 });
    // an unpopular opinion on a placard: PROTECTED
    if (t >= tUn) {
      const h = 440, x = 1500, y = 470, s = pop(T, tUn, 0.35, 2.2);
      Civic.prop('protest_sign', x, y, h, { s, rot: 0.03, seed: 1040 });
      if (s > 0.9) {
        g.save(); g.translate(x, y - h * 0.2); g.rotate(0.03);
        hand('UNPOPULAR', 0, -34, 50, tUn + 0.1, { color: C.ink, seed: 1041, dur: 0.4 });
        hand('OPINION', 0, 30, 50, tUn + 0.3, { color: C.ink, seed: 1042, dur: 0.4 });
        g.restore();
      }
    }
    stampAt('PROTECTED', 1500, 560, t, tLet, { s: 0.6, rot: -0.1, color: C.navy });
    // speech you hate is still speech
    if (t >= tSp) freedomBadge('speech', 960, 640, 130, t, tSp, 1050, { pulse: S.pulse(t, 7) });
  }

  /* the bridge: the Bill of Rights; Miranda / Gideon / Gault docket slips */
  const SLIPS = [
    { id: 'miranda', word: 'silent', say: 'stay silent', x: 350 },
    { id: 'gideon', word: 'lawyer', say: 'a lawyer', x: 960 },
    { id: 'gault', word: 'hearing', say: 'a fair hearing', x: 1570 },
  ];
  function actBridge(S, t) {
    const C = CC();
    if (!window.Civic) return;
    const tAc = S.W(12, 'accused'), tBi = S.W(12, 'Bill');
    if (t >= tAc) hand("if you're ever accused…", 420, 300, 54, tAc, { color: C.ink, seed: 1101, dur: 0.6 });
    if (t >= tBi) {
      Civic.prop('scroll_rights', 960, 300, 260, { s: pop(T, tBi, 0.35, 2.2), rot: -0.04, seed: 1102 });
      wood('BILL OF RIGHTS', 1450, 300, 44, tBi + 0.1, { color: C.cream, ink: C.brick, seed: 1103, stagger: 0.03 });
    }
    SLIPS.forEach((sl, k) => {
      const t0 = S.W(13, sl.word) - 0.15; if (t < t0) return;
      const K = CASE(sl.id), u = E.outBack(P(t, t0, t0 + 0.4), 1.3), y = lerp(1200, 600, u), rot = [-0.02, 0.015, -0.01][k];
      Civic.docket(sl.x, y, 540, 250, { name: K.name, year: K.year, vote: VOTE(K.vote), eoc: K.eoc, rot, seed: 1110 + k * 10, t0: t0 + 0.3 });
      hand(sl.say, sl.x - 60, y + 28, 56, t0 + 0.2, { color: C.navy, seed: 1140 + k, dur: 0.4, rot });
    });
  }

  /* the outro: the five badges go through as the gate opens; RATIFIED */
  function actOutro(S, t, A) {
    const C = CC();
    if (!window.Civic) return;
    const tRi = S.W(18, 'rights'), p = A.p, go = E.inOutCubic(P(t, p.tOpen0 + 0.3, p.tOpen1 + 0.6));
    Civic.FREEDOMS.forEach((id, i) => {
      const t0 = tRi + i * 0.12; if (t < t0) return;
      const x = lerp(740 + i * 110, 790 + i * 85, go), y = lerp(690, 600, go), s = lerp(1, 0.78, go);
      freedomBadge(id, x, y, 96 * s, t, t0, 1200 + i * 7, { label: false, pulse: S.pulse(t, 7) });
    });
    if (t >= tRi) hand('your rights', 960, 520, 54, tRi, { color: C.navy, seed: 1230, dur: 0.4, outline: 'rgba(243,234,215,0.95)' });
    const tS = A.tStamp;
    if (t >= tS) {
      Civic.seal(1260, 250, 62, { s: pop(T, tS + 0.25, 0.35, 2.4), rot: 0.2, seed: 1241 });
      UI.stamp('RATIFIED', 960, 220, { rot: -0.1, s: pop(T, tS, 0.3, 2.2) * 1.2, color: C.brick });
    }
  }

  function drawStage(S, p, t, docked) {
    backdrop(S, p, t, docked);
    gate(S, t, gateAt(S, p, t));
    snow(S, t);
    const k = actAt(p, t), A = p.acts[k];
    const run = (i, a) => withAlpha(a, () => { g.save(); p.acts[i].draw(S, t, { ...p.acts[i], tStamp: p.tStamp, p }); g.restore(); });
    if (k > 0 && t < A.at + FADE) run(k - 1, 1 - P(t, A.at, A.at + FADE));
    run(k, k === 0 ? 1 : P(t, A.at, A.at + FADE * 0.6));
  }

  /* ---------------- karaoke ---------------- */
  const HOOK = ['schoolhouse', 'gate'];
  const FREE_WORD = { speak: 'speech', speech: 'speech', speaking: 'speech', print: 'press', paper: 'press', gather: 'assembly', petition: 'petition' };
  const wordColor = w => { const f = FREE_WORD[normW(w)]; if (!f || !window.Civic) return null; return f === 'petition' ? GOLD_TEXT : Civic.FREEDOM[f].color; };
  function hookIdx(item) {
    const ws = item.words, out = new Set();
    for (let k = 0; k + HOOK.length <= ws.length; k++) if (HOOK.every((w, j) => normW(ws[k + j].w) === w)) HOOK.forEach((_, j) => out.add(k + j));
    return out;
  }
  function tokens(item) {
    const ws = item.words, hk = hookIdx(item), toks = [];
    for (let k = 0; k < ws.length; k++) {
      if (hk.has(k) && !hk.has(k - 1)) {
        const run = ws.slice(k, k + HOOK.length), last = run[run.length - 1];
        const trail = (last.w.match(/[,.!?]+$/) || [''])[0];
        toks.push({ text: run.map(w => w.w).join(' ').replace(/[,.!?]+$/, '').toUpperCase(), s: run[0].s, e: last.e, ransom: true, trail });
        k += HOOK.length - 1; continue;
      }
      toks.push({ text: ws[k].w, s: ws[k].s, e: ws[k].e, col: wordColor(ws[k].w) });
    }
    return toks;
  }
  const lays = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (lays.has(key)) return lays.get(key);
    const toks = tokens(item), f = `${size}px ${WOOD}`, space = measure(' ', f) + size * 0.08, rs = size * 0.62;
    let x = 0;
    toks.forEach((tk, j) => {
      tk.seed = 400 + item.i * 13 + j * 5;
      if (tk.ransom) {
        tk.w = ransomW(tk.text, rs, tk.seed); tk.tw = tk.trail ? measure(tk.trail, f) : 0;
        tk.x = x + size * 0.1; x += tk.w + tk.tw + size * 0.2 + space;
      } else { tk.w = measure(tk.text, f); tk.x = x; x += tk.w + space; }
    });
    const out = { toks, width: x - space, size, rs };
    if (fontOk()) lays.set(key, out);
    return out;
  }
  const tokEnd = (toks, j) => { const tk = toks[j], nx = toks[j + 1]; return Math.min(Math.max(tk.e, tk.s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  function drawCurrent(item, t, y, maxW = 1780) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2, C = CC();
    const appear = item.appear ?? item.s - 0.5, a = E.outCubic(P(t, appear, appear + 0.25)), yy = y + (1 - a) * 36;
    const f = `${size}px ${WOOD}`;
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < tokEnd(Lo.toks, j);
      if (tk.ransom) {
        if (!sung) withAlpha(0.3 * a, () => { g.font = `${Math.round(size * 0.8)}px ${TYPE}`; g.fillStyle = C.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(tk.text, x + tk.w / 2, yy); });
        else {
          const beat = 1 + 0.06 * Math.exp(-(t - tk.s) * 6);
          g.save(); g.translate(x + tk.w / 2, yy); g.scale(beat, beat);
          ransom(tk.text, 0, 0, Lo.rs, tk.s, { seed: tk.seed, stagger: clamp((tk.e - tk.s) / Math.max(1, tk.text.length), 0.02, 0.06) });
          g.restore();
        }
        if (tk.trail && sung) { g.save(); g.font = f; g.fillStyle = C.ink; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(tk.trail, x + tk.w + 6, yy); g.restore(); }
        return;
      }
      if (active) paperRect(x + tk.w / 2, yy + size * 0.04, tk.w + 24, size * 1.08, PAL.mustard, { seed: tk.seed, depth: 0.5, boil: false, s: 1 + 0.06 * Math.exp(-(t - tk.s) * 10) });
      g.save(); g.font = f; g.textAlign = 'left'; g.textBaseline = 'middle';
      if (!sung) { g.globalAlpha *= 0.3 * a; g.fillStyle = C.ink; g.fillText(tk.text, x, yy); }
      else {
        const p = E.outQuad(P(t, tk.s, tk.s + clamp(tk.e - tk.s, 0.08, 0.3)));
        if (p < 1) { g.save(); g.globalAlpha *= 0.3; g.fillStyle = C.ink; g.fillText(tk.text, x, yy); g.restore(); g.beginPath(); g.rect(x - 4, yy - size, (tk.w + 8) * p, size * 2); g.clip(); }
        g.fillStyle = tk.col || C.ink; g.fillText(tk.text, x, yy);
      }
      g.restore();
    });
  }
  /* a line comes up 0.5 s before its first word, but never before the previous line has been sung */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) { const nx = ws[j + 1], end = Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); if (t >= ws[j].s - 0.02 && t < end) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e }; }
    return null;
  }
  function band(song, t) {
    const C = CC();
    paperRect(960, BAND_Y + 140, 2040, 290, C.cream, { seed: 77, torn: true, depth: 1.8, boil: false });
    tape(110, BAND_Y + 6, 150, -0.08, undefined, 71); tape(1810, BAND_Y + 6, 150, 0.08, undefined, 72);
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) { g.save(); g.font = `28px ${TYPE}`; g.fillStyle = 'rgba(31,47,85,0.6)'; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(sec.toLowerCase(), 70, BAND_Y + 36); g.restore(); }
    if (cur) drawCurrent(cur, t, CUR_Y);
    const note = (txt, col, a) => { let size = NEXT_SIZE; const f = s => `${s}px ${TYPE}`; const w = measure(txt, f(size)); if (w > 1700) size = Math.floor(size * 1700 / w);
      g.save(); g.globalAlpha *= a; g.font = f(size); g.fillStyle = col; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt, 960, NEXT_Y); g.restore(); };
    if (next) note(next.text, C.navy, 0.6);
    else if (song.items.length && t > song.items[song.items.length - 1].e) note(END_NOTE, C.navy, 0.75);
    if (!cur && !next && t > 1) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      g.save(); g.font = `72px 'Segoe UI Symbol', 'DejaVu Sans', sans-serif`; g.fillStyle = C.navy; g.textAlign = 'center'; g.textBaseline = 'middle';
      ['♪', '♫', '♪'].forEach((n, i) => g.fillText(n, 860 + i * 100, CUR_Y - 24 * pu * (i === b ? 1 : 0.15)));
      g.restore();
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class Ballad extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0; this.lastT = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      try { ['tinker', 'hazelwood', 'miranda', 'gideon', 'gault'].forEach(CASE); } catch (e) { this.err = 'rights model: ' + e.message; console.warn('[ballad]', this.err); }
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[ballad] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.lastT = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.lastT = this.pos; this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; this.lastT = t; }
    /* stamp the step now (the end of a ≥ 90 % listen, the "Finish" button, QA) */
    done() { if (this.completed) return; this.completed = true; if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    finishStub() { this.done(); }
    finish() { this.done(); }
    canFinish() { return !!this.song && !this.completed && this.heard >= THRESH * this.song.dur; }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }
    gateAt(t) { if (!this.plan) return null; const G = gateAt(this.song, this.plan, t); return { alpha: +G.alpha.toFixed(2), open: +G.open.toFixed(2) }; }
    badgesAt(t) {
      if (!this.plan) return [];
      const A = this.plan.acts[actAt(this.plan, t)];
      if (A.c0 != null) { const bt = badgeTimes(this.song, A.c0); return Civic.FREEDOMS.filter(id => t >= bt[id]); }
      if (A.id === 'outro') return Civic.FREEDOMS.filter((id, i) => t >= this.song.W(18, 'rights') + i * 0.12);
      if (A.id === 'verse2') return t >= this.song.W(11, 'speech') ? ['speech'] : [];
      return [];
    }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      const now = this.now();
      if (this.lastT < this.plan.tStamp && now >= this.plan.tStamp) Audio.play('stamp', { gain: 0.6 });
      this.lastT = now;
      if (now >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= THRESH * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) Game.replace(new Levels.map());
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.finishRect && Math.abs(x - this.finishRect[0]) < this.finishRect[2] / 2 && Math.abs(y - this.finishRect[1]) < this.finishRect[3] / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null; this.lastT = this.pos;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = (Game.content.song && Game.content.song.title) || 'The Schoolhouse Gate';
      if (!this.song || this.err) {
        g.fillStyle = paperTile(CC().parchment, 6); g.fillRect(0, 0, 1920, 1080);
        ransom('THE BALLAD', 960, 400, 110, this.enteredAt + 0.2, { seed: 42 });
        const msg = this.err ? 'the song is missing (' + this.err + ')' : 'tuning the guitar…';
        hand(msg, 960, 580, 54, this.enteredAt + 0.4, { color: CC().navy, seed: 44 });
        if (docked) { Dock.head(title, ''); Dock.text('bl-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawStage(this.song, this.plan, t, docked);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) {
          const hk = hookIdx(cur);
          this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hk.has(j) })) };
        }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? END_NOTE : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the song…' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.ended && Game.current() === this) {
        Dock.buttons('bl-end', [{ label: '↺ Play it again', color: PAL.teal, onClick: () => this.restart() },
          { label: 'Back to the map', color: CC().navy, onClick: () => Game.replace(new Levels.map()) }]);
      } else if (this.canFinish() && Game.current() === this) {
        Dock.buttons('bl-finish', [{ label: 'Finish', color: CC().brick, onClick: () => this.done() }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'song position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150, 1080);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the song from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => Game.replace(new Levels.map()) });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos; this.lastT = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused ▶: a dim wash and a mustard paper disc */
    pausedMark(y, r, h) {
      g.save(); g.fillStyle = 'rgba(27,21,18,0.28)'; g.fillRect(0, 0, 1920, h); g.restore();
      paperCircle(960, y, r, PAL.mustard, { seed: 63, depth: 1.6 });
      g.save(); g.fillStyle = PAL.ink; g.beginPath(); g.moveTo(960 - r * 0.32, y - r * 0.55); g.lineTo(960 + r * 0.55, y); g.lineTo(960 - r * 0.32, y + r * 0.55); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const t0 = this.enteredAt, S = this.song, C = CC();
      ransom('THE BALLAD', 40, 56, 36, t0 + 0.1, { align: 'left', seed: 61 });
      hand('♪ ' + CREDIT, 44, 114, 28, t0 + 0.4, { align: 'left', color: C.navy, seed: 62, dur: 0.4 });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('bl-map', '⌂', m.x, m.y, fx)) { Game.replace(new Levels.map()); return; }
      if (S) {
        if (UI.iconButton('bl-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('bl-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, color: PAL.mustard })) this.toggle();
      }
      if (t == null) return;
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('bl-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 22 : 12, sy = 1080 - sh;
      g.save();
      g.fillStyle = 'rgba(43,36,32,0.25)'; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = C.brick; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = C.navy; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) paperCircle(clamp(1920 * t / S.dur, 26, 1894), touch ? 1080 - 24 : sy + sh / 2, touch ? 22 : 14, C.navy, { seed: 66, depth: 0.8, boil: false });
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100, BAND_Y);
        hand(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, 48, -1e6, { dur: 0.01, color: PAL.cream, outline: 'rgba(31,47,85,0.92)', seed: 64 });
      }
      if (!this.startedOnce && !this.frozen) hand('loading the song…', 960, 590, 48, this.enteredAt + 0.3, { color: C.navy, seed: 65 });
      this.finishRect = null;
      if (this.ended && Game.current() === this) {
        if (UI.button('bl-again', '↺ Play it again', 700, 640, { w: 440, h: 110, color: PAL.teal, size: 56 })) this.restart();
        if (UI.button('bl-back', 'Back to the map', 1220, 640, { w: 440, h: 110, color: C.navy, size: 56 })) Game.replace(new Levels.map());
      } else if (this.canFinish() && Game.current() === this) {
        // top right, under the HUD buttons: clear of the stamp and the lyrics
        const bw = 300, bh = UI.tap(90), bx = 1920 - 40 - bw / 2, by = m.y + m.r + 28 + bh / 2;
        this.finishRect = [bx, by, bw, bh];
        if (UI.button('bl-finish', 'Finish', bx, by, { w: bw, h: 90, color: C.brick, size: 52 })) this.done();
      }
    }
  }
  Ballad.STEP = STEP;
  Ballad.TAKE = TAKE_NAME;
  Ballad.Song = Song;
  Ballad.plan = plan;
  Levels.ballad = Ballad;
})();
